import i18n from 'i18next'
import { toast } from 'react-toastify'

/*
 * Aus einem fehlgeschlagenen Aufruf eine Meldung machen, die man lesen kann.
 *
 * Vorher stand in jeder Fehlermeldung der Satz der Programmbibliothek vorneweg
 * („Anfrage fehlgeschlagen mit Statuscode 401."). Der sagt niemandem etwas, der
 * nicht selbst entwickelt — und er verdeckte den verständlichen Teil dahinter.
 *
 * Die Regel hier: Sagt der Server etwas Verständliches, steht genau das da.
 * Sagt er nichts oder etwas, das für Entwickler:innen geschrieben ist, wird der
 * Statuscode gedeutet — „Dafür fehlt die Berechtigung" statt „403".
 */

/** Deutung je Statuscode, wenn vom Server nichts Brauchbares kommt. */
const BY_STATUS = {
	400: 'Please check your entries and try again.',
	401: 'Your session has expired. Please sign in again.',
	403: 'You do not have permission to do this.',
	404: 'This entry no longer exists. It may have been deleted in the meantime.',
	409: 'This does not work because there is already an entry for it.',
	413: 'The file is too large.',
	415: 'This file format is not supported.',
	422: 'Please check your entries and try again.',
	429: 'Too many requests in a short time. Please wait a moment and try again.',
	500: 'Something went wrong on the server. Please try again later.',
	502: 'Something went wrong on the server. Please try again later.',
	503: 'The server cannot be reached at the moment. Please try again in a few minutes.',
	504: 'The server cannot be reached at the moment. Please try again in a few minutes.'
}

export const GENERIC = 'Something went wrong. Please try again.'
export const OFFLINE = 'No connection to the server. Please check your internet connection and try again.'
export const TIMEOUT = 'The server did not respond in time. Please try again.'

/*
 * Kennzeichen einer Meldung, die für Entwickler:innen geschrieben wurde. Sie
 * landet sonst unverändert auf dem Bildschirm — das Backend reicht bei einem
 * unerwarteten Fehler die Meldung der Programmbibliothek durch (index.js:
 * `message: error.message`).
 */
const TECHNICAL = [
	/[A-Za-z]*Error\b/, // TypeError, SequelizeDatabaseError …
	/\bCannot read\b/i,
	/\b(?:undefined|null|NaN)\b/, // „Null" im deutschen Satz ist großgeschrieben
	/\b(?:Sequelize|SQL|ECONN[A-Z]*|ENOENT|EADDR|ER_[A-Z_]+)/,
	/\bat\s+\w+[\s(]/, // Zeile aus einem Aufrufstapel
	/^Validation error\./i, // Sammelmeldung des Prüfbausteins, Feld für Feld englisch
	/^[A-Z][A-Z0-9_]{3,}$/, // INTERNAL_SERVER_ERROR
	/[{}<>[\]]|\bJSON\b/
]

const isTechnical = text => TECHNICAL.some(muster => muster.test(text))

/**
 * Verständliche Meldung zu einem fehlgeschlagenen Aufruf.
 *
 * @param {*} error     Fehler aus axios
 * @param {string} fallback  eigener Satz, wenn sich nichts Genaueres sagen lässt
 *                           (englischer i18n-Schlüssel wie überall im Projekt)
 * @returns {string} übersetzter Text, direkt anzeigbar
 */
export const errorText = (error, fallback) => {
	// Ohne Antwort kam die Anfrage nicht bis zum Server — dann liegt es an der
	// Verbindung, nicht am Vorgang.
	if (!error?.response) {
		if (error?.code === 'ECONNABORTED' || /timeout/i.test(error?.message || '')) {
			return i18n.t(TIMEOUT)
		}
		return i18n.t(OFFLINE)
	}

	const data = error.response.data || {}
	const raw = (data.message || data.error?.description || '').trim()

	/*
	 * Ein bekannter Satz ist übersetzt und damit erwiesenermaßen für Menschen
	 * gedacht — der hat Vorrang, auch wenn er ein technisch klingendes Wort
	 * enthält („Token is not valid").
	 */
	if (raw && i18n.exists(raw)) return i18n.t(raw)
	if (raw && !isTechnical(raw)) return raw

	return i18n.t(BY_STATUS[error.response.status] || fallback || GENERIC)
}

/**
 * Meldung anzeigen. Über die `toastId` erscheint derselbe Satz nur einmal —
 * sonst stapeln sich bei einem Fehler zwei Meldungen übereinander, weil neben
 * dem allgemeinen Weg (`http.js`) auch die auslösende Stelle etwas sagt.
 */
export const notifyError = (error, fallback) => {
	const text = errorText(error, fallback)
	toast.error(text, { toastId: text })
	return text
}
